import axios from "axios";
import { getStoredAuthSession } from "../auth/authSession";

const getBaseUrl = () => process.env.REACT_APP_BASE_URL || "https://services.allhomedesire.com";

const getAuthHeaders = () => {
  const session = getStoredAuthSession();
  const token = session?.accessToken || session?.token || session?.refreshToken;
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * 1.1 Fetch All Network Partners (Non-Customer Users)
 * Fallback to customer agent list if customer user
 */
export const fetchNetworkPartners = async ({
  page_number = 1,
  page_size = 50,
  order_by = "DESC",
  isCustomer = false,
} = {}) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();

  if (isCustomer) {
    try {
      const res = await axios.get(`${baseUrl}/customer/agent/list/network`, { headers });
      const partners = res.data?.users || res.data?.networks || res.data?.agents || [];
      return {
        success: true,
        data: Array.isArray(partners) ? partners : [],
        total: Array.isArray(partners) ? partners.length : 0,
      };
    } catch (err) {
      console.warn("Falling back to /user/network/all for customer:", err);
    }
  }

  const res = await axios.post(
    `${baseUrl}/user/network/all`,
    { page_number, page_size, order_by },
    { headers }
  );

  const networks = res.data?.networks || res.data?.users || [];
  return {
    success: true,
    data: Array.isArray(networks) ? networks : [],
    totalRecord: res.data?.totalRecord || (Array.isArray(networks) ? networks.length : 0),
    totalPage: res.data?.totalPage || 1,
  };
};

/**
 * 1.2 Fetch Agent / Partner List (Customer Users)
 */
export const fetchCustomerAgentListNetwork = async () => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.get(`${baseUrl}/customer/agent/list/network`, { headers });
  return res.data;
};

/**
 * 1.3 Fetch Contact List (Customer Users)
 */
export const fetchCustomerAgentListContact = async () => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.get(`${baseUrl}/customer/agent/list/contact`, { headers });
  return res.data;
};

/**
 * 1.4 Search Partner by Contact / Name
 */
export const searchPartnersByContact = async ({
  keyword_search = "",
  page_number = 1,
  page_size = 100,
  order_by = "ASC",
  user_type = "PARTNER",
} = {}) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.post(
    `${baseUrl}/admin/users/searchByContact`,
    {
      page_number,
      page_size,
      order_by,
      keyword_search,
      user_type,
    },
    { headers }
  );
  return res.data;
};

/**
 * 1.5 Add / Invite Partner to Network
 */
export const addPartnerToNetwork = async (partnerData) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const payload = {
    first_name: partnerData.first_name || "",
    last_name: partnerData.last_name || "",
    mobile_number: partnerData.mobile_number || "",
    email_address: partnerData.email_address || "",
    address: partnerData.address || "",
    address1: partnerData.address1 || "",
    street: partnerData.street || "",
    apartment: partnerData.apartment || "",
    country_name: partnerData.country_name || "India",
    state_name: partnerData.state_name || "",
    city_name: partnerData.city_name || "",
    zip_code: partnerData.zip_code || "",
    area_name: partnerData.area_name || "",
  };

  const res = await axios.post(`${baseUrl}/user/network/add`, payload, { headers });
  return res.data;
};

/**
 * 1.6 Hit Partner Addition Reward Points
 */
export const triggerPartnerReward = async () => {
  try {
    const baseUrl = getBaseUrl();
    const headers = getAuthHeaders();
    const res = await axios.post(`${baseUrl}/customer/rewards/user/ADD_PARTNER`, {}, { headers });
    return res.data;
  } catch (err) {
    console.warn("Reward points API call completed with notice:", err?.message);
    return { statusType: "SKIPPED" };
  }
};

/**
 * 1.7 Get Network Partner Details by ID
 */
export const getNetworkPartnerById = async (networkId) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.get(`${baseUrl}/user/network/id/${networkId}`, { headers });
  return res.data;
};

/**
 * 1.8 Edit / Update Network Partner Details
 */
export const editNetworkPartner = async (partnerData) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const payload = {
    id: partnerData.id,
    first_name: partnerData.first_name || "",
    last_name: partnerData.last_name || "",
    mobile_number: partnerData.mobile_number || "",
    email_address: partnerData.email_address || "",
    country_name: partnerData.country_name || "",
    state_name: partnerData.state_name || "",
    city_name: partnerData.city_name || "",
    area_name: partnerData.area_name || "",
  };
  const res = await axios.put(`${baseUrl}/user/network/edit`, payload, { headers });
  return res.data;
};

/**
 * 1.9 Delete Partner from Network
 */
export const deleteNetworkPartner = async (networkId) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.delete(`${baseUrl}/user/network/delete/${networkId}`, { headers });
  return res.data;
};

/**
 * 2. Main Network Screen Header User Details API
 */
export const fetchHeaderUserDetails = async (userId) => {
  if (!userId) return null;
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.get(`${baseUrl}/admin/user/id/${userId}`, { headers });
  return res.data;
};

/**
 * 3.1 Sub-Tab: Customers / Friends Tab APIs
 */
export const fetchContacts = async ({
  page_number = 1,
  page_size = 50,
  order_by = "DESC",
  isCustomer = false,
} = {}) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();

  if (isCustomer) {
    try {
      const res = await axios.get(`${baseUrl}/customer/agent/list/contact`, { headers });
      const list = res.data?.contacts || res.data?.users || res.data?.agents || [];
      return {
        success: true,
        data: Array.isArray(list) ? list : [],
        total: Array.isArray(list) ? list.length : 0,
      };
    } catch (err) {
      console.warn("Falling back to /user/contact/all:", err);
    }
  }

  const res = await axios.post(
    `${baseUrl}/user/contact/all`,
    { page_number, page_size, order_by },
    { headers }
  );
  const contacts = res.data?.contacts || [];
  return {
    success: true,
    data: Array.isArray(contacts) ? contacts : [],
    totalRecord: res.data?.totalRecord || (Array.isArray(contacts) ? contacts.length : 0),
    totalPage: res.data?.totalPage || 1,
  };
};

export const addContact = async (contactData) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const payload = {
    first_name: contactData.first_name || "",
    last_name: contactData.last_name || "",
    mobile_number: contactData.mobile_number || "",
    email_address: contactData.email_address || "",
    address: contactData.address || "",
    area_name: contactData.area_name || "",
    zip_code: contactData.zip_code || "",
  };
  const res = await axios.post(`${baseUrl}/user/contact/add`, payload, { headers });
  return res.data;
};

export const editContact = async (contactData) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const payload = {
    id: contactData.id,
    first_name: contactData.first_name || "",
    last_name: contactData.last_name || "",
    mobile_number: contactData.mobile_number || "",
    email_address: contactData.email_address || "",
    address: contactData.address || "",
    area_name: contactData.area_name || "",
    zip_code: contactData.zip_code || "",
  };
  const res = await axios.put(`${baseUrl}/user/contact/edit`, payload, { headers });
  return res.data;
};

export const deleteContact = async (contactId) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.delete(`${baseUrl}/user/contact/delete/${contactId}`, { headers });
  return res.data;
};

/**
 * 3.2 Sub-Tab: Teams Tab APIs
 */
export const fetchPartnerTeams = async ({ page = 1, size = 50, search = "" } = {}) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.post(
    `${baseUrl}/api/partner/team/partnerTeams`,
    { page, size, search },
    { headers }
  );
  const teams = res.data?.partnerTeams || [];
  return {
    success: true,
    data: Array.isArray(teams) ? teams : [],
    totalRecord: res.data?.totalRecord || (Array.isArray(teams) ? teams.length : 0),
    totalPage: res.data?.totalPage || 1,
  };
};

export const fetchTeamMembers = async (
  teamId,
  { page_number = 1, page_size = 1000, order_by = "DESC" } = {}
) => {
  if (!teamId) return { data: [] };
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.post(
    `${baseUrl}/api/partner/team/mmeber/team/${teamId}`,
    { page_number, page_size, order_by },
    { headers }
  );
  const members = res.data?.partnerTeamMembers || res.data?.members || [];
  return {
    success: true,
    data: Array.isArray(members) ? members : [],
  };
};

export const createPartnerTeam = async ({
  name,
  description = "",
  total_seats = 5,
  used_seats = 0,
  available_seats = 5,
  status = "ACTIVE",
}) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const payload = {
    name,
    description,
    total_seats: Number(total_seats) || 5,
    used_seats: Number(used_seats) || 0,
    available_seats: Number(available_seats) || Number(total_seats) || 5,
    status,
  };
  const res = await axios.post(`${baseUrl}/api/partner/team`, payload, { headers });
  return res.data;
};

export const deletePartnerTeam = async (teamId) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.delete(
    `${baseUrl}/api/partner/team/deletePartnerTeam/${teamId}`,
    { headers }
  );
  return res.data;
};

export const addTeamMember = async ({
  team_id,
  first_name,
  last_name = "",
  mobile_number,
  email_address = "",
  role = "MEMBER",
}) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const payload = {
    team_id,
    first_name,
    last_name,
    mobile_number,
    email_address,
    role,
  };
  const res = await axios.post(`${baseUrl}/api/partner/team/member/add`, payload, { headers });
  return res.data;
};

export const deleteTeamMember = async (memberId) => {
  const baseUrl = getBaseUrl();
  const headers = getAuthHeaders();
  const res = await axios.delete(`${baseUrl}/api/partner/team/member/${memberId}`, { headers });
  return res.data;
};
